import { chartSelectionKeys, tableSelectionKeys } from '@/components/Screener/helper';
import { defaultFirstCols } from '@/components/Screener/useScreenerTableConfiguration';
import { defaultColOrder, defaultColToIgnore } from '@/pages/screener/helper';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ColumnPinningState } from '@tanstack/react-table';
import { RootState } from '../../../app/store';
import { PresetCriterias, ScreenerDisplayType, ScreenerType } from './screenerTypes';

// Helper function to get default state for a given asset type
const getDefaultState = (assetType: ScreenerType): PresetCriterias => ({
  type: assetType,
  columns_to_ignore: defaultColToIgnore[assetType],
  order: defaultColOrder[assetType],
  column_pinning: { left: ['select', ...defaultFirstCols[assetType]], right: [] },
});

const initialState: Record<ScreenerType, PresetCriterias> & { type?: ScreenerType } = {
  funds: getDefaultState('funds'),
  buildings: getDefaultState('buildings'),
  transactions: getDefaultState('transactions'),
  dwellings: getDefaultState('dwellings'),
  lots: getDefaultState('lots'),
};

function setState<K extends keyof T, T, V extends T[K]>({
  state,
  key,
  value,
}: {
  state: T;
  key: K;
  value: V;
}) {
  return (state[key] = value);
}

export const screenerSlice = createSlice({
  name: 'screener',
  initialState,
  reducers: {
    updateScreener: (state, action: PayloadAction<{ selections: PresetCriterias }>) => {
      const selections = action.payload.selections;
      const assetType = selections.type ?? state.type;

      if (assetType !== state.type) state.type = assetType;
      if (!assetType) return;

      const keys = Object.keys(selections) as Array<keyof PresetCriterias>;
      const stateToBeChanged = state[assetType] ?? {};

      keys.map((key) => setState({ state: stateToBeChanged, key, value: selections[key] }));
    },
    hardResetScreener: (state) => {
      const assetType = state.type;

      if (!assetType) return;
      state[assetType] = getDefaultState(assetType);
    },
    resetScreener: (state, action: PayloadAction<ScreenerDisplayType | 'chart'>) => {
      const selectionType = action.payload;
      const assetType = state.type;

      if (!assetType) return;
      const currentSelections = state[assetType];

      const keys =
        selectionType === 'table'
          ? tableSelectionKeys
          : selectionType === 'filter'
            ? (Object.keys(currentSelections) as Array<keyof PresetCriterias>).filter(
                (key) =>
                  key === 'columns_to_ignore' ||
                  (!tableSelectionKeys.includes(key) && !chartSelectionKeys.includes(key))
              )
            : chartSelectionKeys;

      const emptySelection: Partial<
        Record<keyof PresetCriterias, string[] | ColumnPinningState | ScreenerType>
      > = {
        type: assetType,
        columns_to_ignore: defaultColToIgnore[assetType],
        order: defaultColOrder[assetType],
        column_pinning: { left: ['select', ...defaultFirstCols[assetType]], right: [] },
      };

      keys.map((key) => setState({ state: currentSelections, key, value: emptySelection[key] }));
    },
    setScreener: (state, action: PayloadAction<{ selections?: PresetCriterias }>) => {
      const assetType = action.payload.selections?.type;
      if (assetType !== state.type) state.type = assetType;

      if (!assetType) return;
      state[assetType] = action.payload.selections || getDefaultState(assetType);
    },
  },
});

export const selectScreenerCriterias = (state: RootState) => {
  const assetType = state.screener.type;
  return assetType ? state.screener[assetType] : {};
};

export const { updateScreener, hardResetScreener, resetScreener, setScreener } =
  screenerSlice.actions;

export const screenerReducer = screenerSlice.reducer;
